import { useEffect, useRef, useState, useCallback } from 'react';

interface UseScrollAnchoringOptions {
  /**
   * Whether to enable scroll anchoring
   */
  enabled?: boolean;
  /**
   * Whether to force immediate scroll anchoring (requires user to scroll first)
   */
  immediate?: boolean;
  /**
   * Whether to show the down arrow indicator
   */
  showDownIndicator?: boolean;
  /**
   * Whether to scroll to bottom on initial load
   */
  scrollToBottomOnLoad?: boolean;
  /**
   * CSS class to apply to the container
   */
  containerClass?: string;
  /**
   * CSS class to apply to the anchor element
   */
  anchorClass?: string;
  /**
   * ID of the scrollable container (optional)
   */
  scrollableContainerId?: string;
}

interface UseScrollAnchoringReturn {
  /**
   * Ref to attach to the scrollable container
   */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /**
   * Ref to attach to the anchor element
   */
  anchorRef: React.RefObject<HTMLDivElement | null>;
  /**
   * CSS classes to apply
   */
  containerClassName: string;
  anchorClassName: string;
  /**
   * Whether to show the down arrow indicator
   */
  showDownIndicator: boolean;
  /**
   * Function to scroll to bottom
   */
  scrollToBottom: () => void;
}

/**
 * Hook for implementing CSS-based scroll anchoring with down arrow indicator
 * Based on CSS-Tricks article: https://css-tricks.com/books/greatest-css-tricks/pin-scrolling-to-bottom/
 *
 * This approach uses CSS overflow-anchor property instead of JavaScript scrolling,
 * providing better performance and avoiding the need to manually scroll on content updates.
 */
export const useScrollAnchoring = (
  options: UseScrollAnchoringOptions = {},
): UseScrollAnchoringReturn => {
  const {
    enabled = true,
    immediate = false,
    showDownIndicator: showIndicator = true,
    scrollToBottomOnLoad = false,
    containerClass = 'scroll-anchor-container',
    anchorClass = 'scroll-anchor',
    scrollableContainerId,
  } = options;

  const containerRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [showDownIndicator, setShowDownIndicator] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Get the scrollable container by ID or use the container ref
  const getScrollableContainer = useCallback((): HTMLElement | null => {
    if (scrollableContainerId) {
      return document.getElementById(scrollableContainerId);
    }
    return containerRef.current;
  }, [scrollableContainerId]);

  // Check scroll position and update indicator
  const updateScrollIndicator = useCallback(() => {
    const scrollableContainer = getScrollableContainer();
    if (!scrollableContainer || !showIndicator) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = scrollableContainer;

    // Check if we're at the bottom (with a small tolerance)
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 5;
    const hasScrollbar = scrollHeight > clientHeight;

    // Show indicator if we have a scrollbar and we're not at the bottom
    const shouldShow = hasScrollbar && !isAtBottom;

    setShowDownIndicator(shouldShow);
  }, [showIndicator, getScrollableContainer]);

  // Scroll to bottom
  const scrollToBottom = useCallback(
    (smooth = true) => {
      const scrollableContainer = getScrollableContainer();
      if (scrollableContainer) {
        scrollableContainer.scrollTo({
          top: scrollableContainer.scrollHeight,
          behavior: smooth ? 'smooth' : 'auto',
        });
      }
    },
    [getScrollableContainer],
  );

  // Force immediate scroll anchoring if requested
  useEffect(() => {
    if (enabled && immediate) {
      const scrollableContainer = getScrollableContainer();
      if (scrollableContainer) {
        // Force a slight scroll to enable scroll anchoring
        scrollableContainer.scrollTop = 1;
      }
    }
  }, [enabled, immediate, getScrollableContainer]);

  // Set up scroll event listener for indicator
  useEffect(() => {
    if (!enabled || !showIndicator) {
      return;
    }

    const scrollableContainer = getScrollableContainer();
    if (!scrollableContainer) {
      return;
    }

    const handleScroll = () => {
      updateScrollIndicator();
    };

    scrollableContainer.addEventListener('scroll', handleScroll, {
      passive: true,
    });

    // Initial check
    updateScrollIndicator();

    return () => {
      scrollableContainer.removeEventListener('scroll', handleScroll);
    };
  }, [enabled, showIndicator, updateScrollIndicator, getScrollableContainer]);

  // Update indicator when content changes
  useEffect(() => {
    if (!enabled || !showIndicator) {
      return;
    }

    // Use a small delay to ensure DOM updates are complete
    const timer = setTimeout(updateScrollIndicator, 100);
    return () => clearTimeout(timer);
  }, [enabled, showIndicator, updateScrollIndicator]);

  // Scroll to bottom on initial load if requested
  useEffect(() => {
    if (scrollToBottomOnLoad && !hasInitialized) {
      scrollToBottom(false);
      setHasInitialized(true);
    }
  }, [scrollToBottomOnLoad, hasInitialized, scrollToBottom]);

  const containerClassName = enabled
    ? `${containerClass} ${immediate ? 'scroll-anchor-immediate' : ''}`.trim()
    : '';

  return {
    containerRef,
    anchorRef,
    containerClassName,
    anchorClassName: enabled ? anchorClass : '',
    showDownIndicator,
    scrollToBottom,
  };
};
